import { ref } from "vue";
import {
  DEFAULT_EMPTY_ROWS,
  DEFAULT_GRID_GROUP,
  GRID_OPEN_FIRST_X_GROUPS,
} from "./learningObjectivesConstants";
import { Cluster } from "./clusterModel";

export const createGridManager = () => {
  const grid = ref([]);
  const weeks = ref([]);
  const isInitialized = ref(false);
  const hasGridGroups = ref(false);
  const isRendered = ref(false);

  const initializeGrid = (items, _weeks, groups = null) => {
    weeks.value = _weeks;

    // Generate the grid without groups
    if (!groups || groups.length === 0) {
      const rows = generateGridRows(items);

      rows.forEach((row) => grid.value.push(row));

      items.forEach((item) => {
        addGridItem(item);
      });

      return grid.value;
    }

    // Set the flag to indicate that the grid has groups
    hasGridGroups.value = true;

    // Generate the grid with groups
    grid.value = groups.map((group, index) => {
      const {
        id,
        meta: { subject = null },
      } = group;

      group.rows = generateGridRows(
        items.filter(
          ({ subject_shortage, planningMeta: { groupId } }) =>
            groupId && groupId === id && subject_shortage === subject,
        ),
        index,
        { groupId: id, subjectShortage: subject },
      );

      return group;
    });

    items.forEach((item) => {
      addGridItem(item);
    });

    // Open the first X groups
    for (
      let index = 0;
      index < Math.min(grid.value.length, GRID_OPEN_FIRST_X_GROUPS);
      index++
    ) {
      toggleGroupVisibility(grid.value[index]);
    }

    return grid.value;
  };

  const generateGridRows = (
    items,
    groupIndex = null,
    additionalData = null,
  ) => {
    const rows = [];

    const rowCount = generateRowSize(items);

    for (let i = 0; i <= rowCount + DEFAULT_EMPTY_ROWS; i++) {
      rows.push(generateClusterRow(i, groupIndex, additionalData));
    }

    return rows;
  };

  const generateClusterRow = (
    rowIndex,
    groupIndex = null,
    additionalData = null,
  ) => {
    return weeks.value.map((_, index) => ({
      id: `${rowIndex}-${index}`,
      weekIndex: index,
      rowIndex,
      ...(groupIndex !== null && { groupIndex }),
      ...(additionalData !== null && { additionalData }),
    }));
  };

  const generateWeekArray = (
    rowIndex,
    weekIndexes,
    groupIndex = null,
    additionalData = null,
  ) => {
    return weekIndexes.map((weekIndex) => ({
      id: `${rowIndex}-${weekIndex}`,
      weekIndex,
      rowIndex,
      ...(groupIndex !== null && { groupIndex }),
      ...(additionalData !== null && { additionalData }),
    }));
  };

  const generateGridGroup = ({ id, name, meta }) => {
    if (!id || !name) {
      throw new Error("Id and name are required to create a grid group");
    }

    return Object.assign({}, DEFAULT_GRID_GROUP, { id, name, meta });
  };

  const toggleGroupVisibility = (group) => {
    group.meta.isVisible = !group.meta.isVisible;

    return group.meta.isVisible;
  };

  const findGridGroupIdByIndex = (groupIndex) => {
    const { rows, ...group } = grid.value[groupIndex];

    return group;
  };

  const addGridItem = (
    item,
    _rowIndex = null,
    _weekIndex = null,
    _groupIndex = null,
  ) => {
    if (
      item?.planningMeta.rowIndex === undefined ||
      item?.planningMeta?.weekIndexes === undefined ||
      item.planningMeta.weekIndexes.length === 0
    ) {
      throw new Error(
        "Row index and week indexes are required to add an item to the grid",
      );
    }

    try {
      const {
        duration,
        subject_shortage,
        planningMeta: { weekIndexes, rowIndex, groupId },
      } = item;

      // Prefer the parameters over the item's values
      _rowIndex = !isNaN(parseInt(_rowIndex)) ? _rowIndex : rowIndex;
      _weekIndex = !isNaN(parseInt(_weekIndex)) ? _weekIndex : weekIndexes[0];
      _groupIndex = !isNaN(parseInt(_groupIndex))
        ? _groupIndex
        : findGridGroupIndex(groupId, subject_shortage);

      let currentRow = grid.value[_rowIndex];

      if (hasGridGroups.value) {
        currentRow = grid.value[_groupIndex].rows[_rowIndex];

        // Update the group index and id
        item.updateGroupIndex(_groupIndex);
      }

      const foundWeekIndex = currentRow.findIndex(
        ({ weekIndex }) => _weekIndex === weekIndex,
      );

      // We return only the conflicting items that are clusters
      const conflictingItems = getAllConflictingRowItems(
        currentRow,
        foundWeekIndex,
        duration,
      ).filter((conflictingItem) => conflictingItem instanceof Cluster);

      if (conflictingItems.length > 0) {
        conflictingItems.forEach((item) => {
          item.onDragStart().onDragDrop(null, {
            rowIndex: item.planningMeta.rowIndex + 1,
            weekIndex: item.planningMeta.weekIndexes[0],
            groupIndex: item.planningMeta.groupIndex,
          });
        });
      }

      currentRow.splice(foundWeekIndex, duration, item);

      if (hasGridGroups.value) {
        const isLastIndex =
          _rowIndex === grid.value[_groupIndex].rows.length - 1;

        if (isLastIndex) {
          const generatedRow = generateClusterRow(
            grid.value[_groupIndex].rows.length,
            _groupIndex,
            { groupId, subjectShortage: subject_shortage },
          );

          grid.value[_groupIndex].rows.push(generatedRow);
        }
      }
    } catch (error) {
      console.log("Cluster kan niet toegevoegd worden want: ", error);
    }
  };

  const getAllConflictingRowItems = (
    row = null,
    weekIndex = null,
    duration = null,
  ) => {
    if (row == null || weekIndex == null || duration == null) {
      throw new Error(
        "Row, week index and duration are required to get conflicting items",
      );
    }

    const conflictingItems = [];

    for (var index = weekIndex; index < row.length; index++) {
      const currentItem = row[index];
      const currentLoopWeekIndex =
        currentItem?.planningMeta?.weekIndexes[0] ?? currentItem?.weekIndex;

      if (currentLoopWeekIndex > weekIndex + duration - 1) {
        break;
      }

      conflictingItems.unshift(currentItem);
    }

    return conflictingItems;
  };

  const generateRowSize = (items) => {
    if (items.every((item) => !item.planningMeta.rowIndex)) {
      return 0;
    }

    return Math.max(
      ...items.flatMap(({ planningMeta: { rowIndex } }) => rowIndex),
    );
  };

  const removeWeekFromRow = (rowIndex, weekIndex, groupIndex = null) => {
    const foundIndex = findWeekIndex(rowIndex, weekIndex, groupIndex);

    if (groupIndex === null) {
      return grid.value[rowIndex].splice(foundIndex, 1);
    }

    return grid.value[groupIndex].rows[rowIndex].splice(foundIndex, 1);
  };

  const addWeekToRow = (model, rowIndex, weekIndex, atEnd = false) => {
    let foundIndex = -1;
    let groupIndex = null;

    if (!hasGridGroups.value) {
      foundIndex = findClusterIndex(rowIndex, model.id);
    } else {
      groupIndex = model.planningMeta.groupIndex;

      foundIndex = findClusterIndex(rowIndex, model.id, groupIndex);
    }

    weekIndex = atEnd ? weekIndex + 1 : weekIndex - 1;
    foundIndex = atEnd ? foundIndex + 1 : foundIndex;

    if (!hasGridGroups.value) {
      return grid.value[rowIndex].splice(foundIndex, 0, {
        weekIndex,
        rowIndex,
      });
    }

    const weekToAdd = generateWeekArray(rowIndex, [weekIndex], groupIndex, {
      groupId: model.planningMeta.groupId,
    })[0];

    return grid.value[groupIndex].rows[rowIndex].splice(
      foundIndex,
      0,
      weekToAdd,
    );
  };

  const findWeekIndex = (rowIndex, weekIndex, groupIndex = null) => {
    let foundIndex = -1;

    if (groupIndex === null) {
      foundIndex = grid.value[rowIndex].findIndex(
        ({ weekIndex: _weekIndex }) => _weekIndex === weekIndex,
      );
    } else {
      foundIndex = grid.value[groupIndex].rows[rowIndex].findIndex(
        ({ weekIndex: _weekIndex }) => _weekIndex === weekIndex,
      );
    }

    if (foundIndex === -1) {
      throw new Error(
        `Week with index ${weekIndex} not found in row ${rowIndex}, skipping removal.`,
      );
    }

    return foundIndex;
  };

  const removeItemFromGrid = (rowIndex, weekIndexes, model = null) => {
    if (!model) {
      throw new Error("Model is required to remove an item from the grid");
    }

    if (!hasGridGroups.value) {
      // This will generate a new array with the weeks that should fill the gaps from the removed model
      const weeks = generateWeekArray(rowIndex, weekIndexes);

      const clusterIndex = findClusterIndex(rowIndex, model.id);

      // Replace the model with the weeks array
      return grid.value[rowIndex].splice(clusterIndex, 1, ...weeks);
    }

    const groupIndex = findGridGroupIndex(
      model.planningMeta.groupId,
      model.subject_shortage,
    );

    // This will generate a new array with the weeks that should fill the gaps from the removed model
    const weeks = generateWeekArray(rowIndex, weekIndexes, groupIndex, {
      groupId: model.planningMeta.groupId,
    });

    const clusterIndex = findClusterIndex(rowIndex, model.id, groupIndex);

    grid.value[groupIndex].rows[rowIndex].splice(clusterIndex, 1, ...weeks);

    return;
  };

  const findGridGroupIndex = (groupId, subject_shortage) => {
    const foundIndex = grid.value.findIndex(
      ({ id, meta: { subject } }) =>
        id === groupId && subject === subject_shortage,
    );

    if (foundIndex === -1) {
      throw new Error(`Group with id ${groupId} not found in the grid`);
    }

    return foundIndex;
  };

  const findClusterIndex = (rowIndex, modelId, groupIndex = null) => {
    let foundIndex = -1;

    if (groupIndex !== null) {
      foundIndex = grid.value[groupIndex].rows[rowIndex].findIndex(
        ({ id }) => id === modelId,
      );
    } else {
      foundIndex = grid.value[rowIndex].findIndex(({ id }) => id === modelId);
    }

    if (foundIndex === -1) {
      throw new Error(
        `Cluster with id ${modelId} not found in row ${rowIndex}, skipping removal.`,
      );
    }

    return foundIndex;
  };

  return {
    grid,
    isInitialized,
    hasGridGroups,
    isRendered,
    initializeGrid,
    generateRowSize,
    generateGridGroup,
    removeWeekFromRow,
    addWeekToRow,
    removeItemFromGrid,
    addGridItem,
    toggleGroupVisibility,
    findGridGroupIdByIndex,
  };
};
